import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext.jsx';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, Filler } from 'chart.js';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { FaChartBar, FaUsers, FaFileExport, FaMicrochip } from 'react-icons/fa';
import { MdWork, MdEvent } from 'react-icons/md';
import Dashboard from './attendance/Dashboard';
import StaffManagement from './attendance/StaffManagement';
import ShiftManagement from './attendance/ShiftManagement';
import HolidayLeave from './attendance/HolidayLeave';
import Reports from './attendance/Reports';
import DeviceManagement from './attendance/DeviceManagement';
import { formatUTCDate, formatUTCTime, isValidDate } from '../utils/dateUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, Filler);

const DEFAULT_SHIFT = { resumptionTime: '08:00', closingTime: '17:00' };
const API_BASE_URL = import.meta.env.VITE_API_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('adminAuthToken') || localStorage.getItem('kioskAuthToken') || localStorage.getItem('authToken')}`,
});

function Attendance({ allowedSections }) {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);

  // Core UI state
  const [activeSection, setActiveSection] = useState(() => allowedSections?.[0] ?? 'dashboard');
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Loading states - separated for better UX
  const [initialLoading, setInitialLoading] = useState(true); // Only for first page render
  const [dataLoading, setDataLoading] = useState(false); // For background data fetching
  const [actionLoading, setActionLoading] = useState(false); // For user actions
  
  // Data states with sensible defaults
  const [staff, setStaff] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [liveLogs, setLiveLogs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);
  
  // 7-day data states
  const [sevenDayData, setSevenDayData] = useState(null);
  const [loadingSevenDay, setLoadingSevenDay] = useState(false);
  
  // Default dashboard stats to render immediately
  const [dashboardStats, setDashboardStats] = useState({
    totalStaff: 0,
    presentToday: 0,
    absentToday: 0,
    lateToday: 0,
  });
  
  const [deviceStatus, setDeviceStatus] = useState({
    isOnline: false,
    lastSeen: null,
    totalRequests: 0,
    attendanceCount: 0
  });
  
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  
  // Report/filter states
  const [reportPage, setReportPage] = useState(1);
  const [reportTotalPages, setReportTotalPages] = useState(1);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportMode, setReportMode] = useState('day');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [shiftPage, setShiftPage] = useState(1);
  const [shiftTotalPages, setShiftTotalPages] = useState(1);
  const [shiftStaffFilter, setShiftStaffFilter] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [editingShift, setEditingShift] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  // Form states
  const [newStaff, setNewStaff] = useState({
    employeeId: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    department: 'Salon',
    position: 'Stylist',
    dateOfEmployment: '',
    employmentType: 'full-time',
    workSchedule: 'Mon-Fri 9:00-17:00',
    bankName: '',
    accountNumber: '',
    accountName: '',
    card: '',
    group: 1,
    timeZone: '0000000100000000',
    verifyMode: 0,
    pin: '',
  });
  const [editingStaff, setEditingStaff] = useState(null);
  const [newShift, setNewShift] = useState({ 
    employeeId: '', 
    selectedDays: [], 
    resumptionTime: '', 
    closingTime: '' 
  });
  const [newLeave, setNewLeave] = useState({ employeeId: '', startDate: '', endDate: '', reason: '' });
  const [editingLeave, setEditingLeave] = useState(null);
  const [newHoliday, setNewHoliday] = useState({ name: '', date: '' });
  const [manualEntry, setManualEntry] = useState({
    employeeId: '',
    date: '',
    checkIn: '',
    checkOut: '',
    status: 'present',
    remarks: '',
  });

  const departments = ['Admin Services', 'Gym', 'Salon', 'Spa', 'IT'];
  const positions = [
    'Manager',
    'Customer Service Assistant',
    'Cashier',
    'Gym Assistant',
    'Coach',
    'Stylist',
    'Cleaner',
    'Security',
    'Social Media Assistant',
    'IT Support',
  ];

  const allSidebarItems = [
    { id: 'dashboard', icon: FaChartBar, label: 'Dashboard' },
    { id: 'staff', icon: FaUsers, label: 'Staff Management' },
    { id: 'shifts', icon: MdWork, label: 'Shift Management' },
    { id: 'holidayleave', icon: MdEvent, label: 'Holidays & Leave' },
    { id: 'reports', icon: FaFileExport, label: 'Reports' },
    { id: 'devices', icon: FaMicrochip, label: 'Device Management' },
  ];

  const sidebarItems = allowedSections
    ? allSidebarItems.filter(item => allowedSections.includes(item.id))
    : allSidebarItems;

  const todayStr = useMemo(() => {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Lagos' });
  }, []);

  // CRITICAL: Immediate page render - no loading dependencies
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Set initial loading to false immediately so page renders
    setInitialLoading(false);
    
    // Start background data fetching without blocking UI
    const startBackgroundLoading = async () => {
      setDataLoading(true);
      try {
        // Fetch staff first as it's needed for other calculations
        await fetchStaffData();
      } catch (err) {
        console.error('Background loading error:', err);
        setError('Failed to load some data. Retrying...');
        // Retry after 3 seconds
        setTimeout(startBackgroundLoading, 3000);
      }
    };

    startBackgroundLoading();
  }, [user, navigate]);

  // Staff data fetcher
  const fetchStaffData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/staff`, {
        headers: getAuthHeaders(),
      });
      const staffData = response.data.staff || response.data || [];
      setStaff(staffData);
      
      // Update dashboard stats immediately with staff count
      setDashboardStats(prev => ({
        ...prev,
        totalStaff: staffData.length,
        absentToday: staffData.length // Default to all absent until we load attendance
      }));
      
      return staffData;
    } catch (err) {
      console.error('Failed to fetch staff:', err);
      throw err;
    }
  }, []);

  // Background data loader - runs after staff is loaded
  useEffect(() => {
    if (staff.length === 0) return;

    const loadBackgroundData = async () => {
      try {
        // Load all secondary data in parallel
        const [attendanceRes, leavesRes, holidaysRes, shiftsRes] = await Promise.allSettled([
          fetchAttendanceSummary(),
          axios.get(`${API_BASE_URL}/api/attendance/leaves`, { headers: getAuthHeaders() }),
          axios.get(`${API_BASE_URL}/api/attendance/holidays`, { headers: getAuthHeaders() }),
          fetchShifts()
        ]);

        // Process results
        if (leavesRes.status === 'fulfilled') {
          setLeaveRequests(leavesRes.value.data || []);
        }
        if (holidaysRes.status === 'fulfilled') {
          setHolidays(holidaysRes.value.data || []);
        }

        setDataLoading(false);
        setError(''); // Clear any loading errors
      } catch (err) {
        console.error('Background data loading failed:', err);
        setDataLoading(false);
        // Don't show error for background loading - just log it
      }
    };

    loadBackgroundData();
  }, [staff.length]);

  // Optimized attendance summary fetcher with absent records support
  const fetchAttendanceSummary = useCallback(async () => {
    try {
      const now = Date.now();
      if (lastFetchTime && (now - lastFetchTime) < 3000) {
        return; // Throttle to max once per 3 seconds
      }

      const params = { 
        mode: reportMode, 
        page: reportPage,
        limit: 50
      };
      
      if (reportMode === 'day') {
        params.startDate = filterDate;
      } else if (reportMode === 'custom') {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      } else {
        params.startDate = new Date().toISOString().split('T')[0];
      }

      console.log('Fetching attendance with params:', params);

      // Hybrid approach: Get attendance records from new endpoint, device status from ADMS
      const [attendanceRes, admsRes] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/api/attendance/report`, {
          headers: getAuthHeaders(),
          params,
          timeout: 10000,
        }),
        axios.get(`${API_BASE_URL}/api/adms/attendance-summary`, {
          headers: getAuthHeaders(),
          params: { mode: reportMode, startDate: params.startDate, endDate: params.endDate },
          timeout: 10000,
        })
      ]);

      // Use attendance records from new endpoint (preserves your absence detection)
      const records = attendanceRes.status === 'fulfilled' ? 
        (attendanceRes.value.data.attendances || []) : [];
      setAttendanceRecords(records);

      // Use device status and live logs from ADMS (preserves working functionality)
      if (admsRes.status === 'fulfilled') {
        const admsData = admsRes.value.data;
        
        // Set device status from ADMS
        setDeviceStatus({
          isOnline: admsData.device?.isOnline || false,
          lastSeen: admsData.device?.lastSeen || null,
          totalRequests: admsData.device?.totalRequests || 0,
          attendanceCount: admsData.device?.attendanceCount || 0,
        });
        
        // Set live logs with photos from ADMS
        const processedLiveLogs = (admsData.recent || []).map(log => ({
          ...log,
          photoUrl: log.photoId ? `${API_BASE_URL}/api/adms/photo/${log.photoId}` : null
        }));
        setLiveLogs(processedLiveLogs);
      } else {
        console.error('Failed to get ADMS data:', admsRes.reason);
        // Fallback: try to get live logs without photos
        try {
          const logsRes = await axios.get(`${API_BASE_URL}/api/attendance/live`, {
            headers: getAuthHeaders(),
            params: { limit: 10 }
          });
          
          const fallbackLogs = (logsRes.data.logs || []).map(log => ({
            ...log,
            photoUrl: null // No photos available from this endpoint
          }));
          setLiveLogs(fallbackLogs);
        } catch (logsError) {
          console.error('Error loading fallback live logs:', logsError);
          setLiveLogs([]);
        }
      }
      
      // Set pagination from attendance endpoint
      if (attendanceRes.status === 'fulfilled') {
        setReportTotalPages(attendanceRes.value.data.totalPages || 1);
      }
      setLastFetchTime(now);
      
      // Calculate stats
      if (staff.length > 0) {
        calculateDashboardStats(staff, records);
        updateChartData(records, 'today');
      }
      
      return records;
    } catch (err) {
      console.error('Failed to fetch attendance summary:', err);
      // Don't throw error, just log it
      setAttendanceRecords([]);
      setLiveLogs([]);
    }
  }, [reportMode, filterDate, customStartDate, customEndDate, reportPage, lastFetchTime, staff]);

  // CORRECTED: Dashboard stats calculation with proper "Not Yet Clocked In" logic - ACTIVE STAFF ONLY
  const calculateDashboardStats = useCallback((staffData, attendanceData) => {
    if (!staffData?.length) {
      setDashboardStats({
        totalStaff: 0,
        presentToday: 0,
        absentToday: 0,
        lateToday: 0,
        notYetClockedIn: 0
      });
      return;
    }

    // FIXED: Filter for active staff only
    const activeStaff = staffData.filter(s => s.status === 'active');
    const totalStaff = activeStaff.length;
    const currentHour = new Date().getHours();
    
    const todayRecords = attendanceData.filter(record => {
      if (!record) return false;
      const recordDate = record.date;
      return recordDate === todayStr || recordDate.startsWith(todayStr);
    });

    // Initialize counters
    const uniqueStaffPresent = new Set(); // Staff who have actually clocked in
    const uniqueStaffAbsent = new Set();  // Staff marked as absent
    const uniqueStaffLate = new Set();    // Staff who were late

    // Process actual attendance records
    todayRecords.forEach(record => {
      if (!record?.employeeId) return;
      
      if (record.absent) {
        // Only count as absent if it's after midday OR if explicitly marked absent
        if (currentHour >= 12 || record.adjustmentNote?.includes('Manual entry')) {
          uniqueStaffAbsent.add(record.employeeId);
        }
      } else if (record.checkIn) {
        // FIXED: Only count as present if they have actually clocked in
        uniqueStaffPresent.add(record.employeeId);
        
        if (record.late) {
          uniqueStaffLate.add(record.employeeId);
        }
      }
      // If record exists but no checkIn and not marked absent, they're "not yet clocked in"
    });

    // Calculate staff who haven't been accounted for yet - ACTIVE STAFF ONLY
    const accountedForStaff = new Set([
      ...uniqueStaffPresent,
      ...uniqueStaffAbsent
    ]);
    
    const activeStaffIds = new Set(activeStaff.map(s => s.employeeId));
    const notYetClockedInCount = activeStaffIds.size - accountedForStaff.size;

    // CORRECTED: Single setDashboardStats call with accurate counts
    setDashboardStats({
      totalStaff,
      presentToday: uniqueStaffPresent.size,      // Only staff who actually clocked in
      absentToday: uniqueStaffAbsent.size,        // Staff marked as absent
      lateToday: uniqueStaffLate.size,           // Staff who were late
      notYetClockedIn: Math.max(0, notYetClockedInCount) // Staff not accounted for
    });

    // Verify math: Present + Absent + NotYetClockedIn should equal TotalStaff
    const calculatedTotal = uniqueStaffPresent.size + uniqueStaffAbsent.size + notYetClockedInCount;
    if (calculatedTotal !== totalStaff) {
      console.warn(`Dashboard stats mismatch: ${calculatedTotal} calculated vs ${totalStaff} actual staff`);
    }
  }, [todayStr]);

  // NEW: Fetch 7-day attendance data in background (doesn't block page load)
  const fetchSevenDayData = useCallback(async () => {
    if (!staff || staff.length === 0) return;
    
    try {
      setLoadingSevenDay(true);
      
      // Calculate date range for last 7 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 6); // Last 7 days including today
      
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      // Fetch attendance data for the 7-day period
      const response = await axios.get(`${API_BASE_URL}/api/attendance/report`, {
        headers: getAuthHeaders(),
        params: {
          mode: 'custom',
          startDate: startDateStr,
          endDate: endDateStr,
          limit: 1000
        }
      });
      
      const records = response.data.attendances || [];
      
      // Get active staff IDs
      const activeStaffIds = new Set(
        staff.filter(s => s.status === 'active').map(s => s.employeeId)
      );
      
      // Filter records to only include active staff
      const activeRecords = records.filter(r => activeStaffIds.has(r.employeeId));
      
      // Process data by date
      const dataByDate = {};
      
      // Initialize all 7 days with zero counts
      for (let i = 0; i < 7; i++) {
        const date = new Date();
        date.setDate(date.getDate() - (6 - i));
        const dateStr = date.toISOString().split('T')[0];
        dataByDate[dateStr] = {
          present: 0,
          absent: 0,
          late: 0,
          total: activeStaffIds.size
        };
      }
      
      // Count records for each date
      activeRecords.forEach(record => {
        const dateStr = record.date;
        if (!dataByDate[dateStr]) return;
        
        if (record.absent) {
          dataByDate[dateStr].absent++;
        } else if (record.checkIn) {
          dataByDate[dateStr].present++;
          if (record.late) {
            dataByDate[dateStr].late++;
          }
        }
      });
      
      setSevenDayData(dataByDate);
      
    } catch (err) {
      console.error('Failed to fetch 7-day data:', err);
      setSevenDayData(null);
    } finally {
      setLoadingSevenDay(false);
    }
  }, [staff]);

  // Trigger 7-day data fetch in background after staff is loaded
  useEffect(() => {
    if (staff.length > 0 && activeSection === 'dashboard') {
      // Small delay to ensure dashboard loads first
      const timer = setTimeout(() => {
        fetchSevenDayData();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [staff.length, activeSection, fetchSevenDayData]);

  // Optimized chart data update
  const updateChartData = useCallback((data, period) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const labels = [];
    const datasets = [];
    const staffMap = new Map(staff.map(s => [s.employeeId, `${s.firstName} ${s.lastName || ''}`]));
    const colors = ['#22C55E', '#EF4444', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899', '#10B981', '#F97316'];

    if (period === 'today') {
      const todayRecords = data.filter(record => {
        if (!record) return false;
        const dateStr = record.date || (record.timestamp && isValidDate(record.timestamp) 
          ? new Date(record.timestamp).toLocaleDateString('en-CA', { timeZone: 'Africa/Lagos' }).split('T')[0] 
          : null);
        return dateStr === todayStr;
      });

      if (todayRecords.length === 0) {
        setChartData({
          labels: [today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'Africa/Lagos' })],
          datasets: [],
        });
        return;
      }

      const uniqueStaff = [...new Set(todayRecords.map(record => record.employeeId))];
      labels.push(today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'Africa/Lagos' }));

      uniqueStaff.forEach((employeeId, index) => {
        const employeeRecord = todayRecords.find(record => record.employeeId === employeeId);
        if (!employeeRecord) return;

        const isPresent = employeeRecord.checkIn || employeeRecord.checkOut || !employeeRecord.absent;
        const isLate = employeeRecord.late && isPresent;
        const isAbsent = employeeRecord.absent || !isPresent;

        datasets.push(
          {
            label: `${staffMap.get(employeeId) || employeeId} (Present)`,
            data: [isPresent ? 1 : 0],
            backgroundColor: colors[index % colors.length],
            stack: 'Stack 0',
          },
          {
            label: `${staffMap.get(employeeId) || employeeId} (Late)`,
            data: [isLate ? 1 : 0],
            backgroundColor: colors[(index + 1) % colors.length],
            stack: 'Stack 1',
          },
          {
            label: `${staffMap.get(employeeId) || employeeId} (Absent)`,
            data: [isAbsent ? 1 : 0],
            backgroundColor: colors[(index + 2) % colors.length],
            stack: 'Stack 2',
          }
        );
      });
    }

    setChartData({ labels, datasets });
  }, [staff, todayStr]);

  // Optimized staff helper functions
  const getStaffName = useCallback((record) => {
    if (!record) return 'Unknown Staff';
    if (record.staffName) return record.staffName;
    
    let employeeId = record.employeeId;
    if (typeof record.employeeId === 'object') {
      employeeId = record.employeeId._id || record.employeeId;
    }
    if (!employeeId && record.staffId) {
      employeeId = record.staffId;
    }
    
    const staffMember = staff.find(s => s.employeeId === employeeId);
    return staffMember ? `${staffMember.firstName} ${staffMember.lastName || ''}`.trim() : 'Unknown Staff';
  }, [staff]);

  const getStaffPosition = useCallback((record) => {
    if (!record) return 'Unknown Position';
    if (record.position) return record.position;
    
    let employeeId = record.employeeId;
    if (typeof record.employeeId === 'object') {
      employeeId = record.employeeId._id || record.employeeId;
    }
    if (!employeeId && record.staffId) {
      employeeId = record.staffId;
    }
    
    const staffMember = staff.find(s => s.employeeId === employeeId);
    return staffMember?.position || 'Unknown Position';
  }, [staff]);

  // Optimized shifts fetcher
  const fetchShifts = useCallback(async () => {
    try {
      const url = `${API_BASE_URL}/api/attendance/shifts?page=${shiftPage}&limit=1000${shiftStaffFilter ? `&staffId=${shiftStaffFilter}` : ''}`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      
      setShifts(Array.isArray(response.data.shifts) ? response.data.shifts : []);
      setShiftTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching shifts:', error);
      // Don't set error for background operations
    }
  }, [shiftPage, shiftStaffFilter]);

  // Auto-refresh functionality for dashboard
  useEffect(() => {
    if (activeSection !== 'dashboard' || staff.length === 0) return;

    const interval = setInterval(() => {
      // Silent refresh - no loading indicators
      fetchAttendanceSummary().catch(err => 
        console.error('Silent refresh failed:', err)
      );
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [activeSection, staff.length, fetchAttendanceSummary]);

  // Generate employee ID function
  const generateEmployeeId = async () => {
    try {
      const existingIds = staff.map(s => s.employeeId);
      let newId = 100;
      while (existingIds.includes(newId.toString().padStart(3, '0'))) {
        newId++;
        if (newId > 999) throw new Error('No available Employee IDs');
      }
      return newId.toString().padStart(3, '0');
    } catch (err) {
      setError('Error generating Employee ID');
      throw err;
    }
  };

  // CRUD operations with action loading
  const handleAddStaff = useCallback(async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      const employeeId = await generateEmployeeId();
      const staffData = {
        ...newStaff,
        employeeId,
        subBusiness: 'Salon',
        bankDetails: {
          bankName: newStaff.bankName,
          accountNumber: newStaff.accountNumber,
          accountName: newStaff.accountName,
        },
      };
      const res = await axios.post(`${API_BASE_URL}/api/staff`, staffData, { headers: getAuthHeaders() });
      setStaff([...staff, res.data]);
      setNewStaff({
        employeeId: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        department: 'Salon',
        position: 'Stylist',
        dateOfEmployment: '',
        employmentType: 'full-time',
        workSchedule: 'Mon-Fri 9:00-17:00',
        bankName: '',
        accountNumber: '',
        accountName: '',
        card: '',
        group: 1,
        timeZone: '0000000100000000',
        verifyMode: 0,
        pin: '',
      });
      setSuccess('Staff added successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
      
      // Silent refresh attendance data
      fetchAttendanceSummary().catch(console.error);
    } catch (err) {
      console.error('Failed to add staff:', err);
      setError('Failed to add staff: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  }, [staff, newStaff, fetchAttendanceSummary]);

  const handleEditStaff = useCallback(async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      const staffData = {
        ...editingStaff,
        bankDetails: {
          bankName: editingStaff.bankName,
          accountNumber: editingStaff.accountNumber,
          accountName: editingStaff.accountName,
        },
      };
      const res = await axios.put(`${API_BASE_URL}/api/staff/${editingStaff._id}`, staffData, { headers: getAuthHeaders() });
      setStaff(staff.map(s => s._id === editingStaff._id ? res.data : s));
      setEditingStaff(null);
      setSuccess('Staff updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
    } catch (err) {
      console.error('Failed to update staff:', err);
      setError('Failed to update staff: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  }, [staff, editingStaff]);

  const handleDeleteStaff = useCallback(async (staffId) => {
    try {
      setActionLoading(true);
      await axios.delete(`${API_BASE_URL}/api/staff/${staffId}`, { headers: getAuthHeaders() });
      setStaff(staff.filter(s => s._id !== staffId));
      setSuccess('Staff deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
    } catch (err) {
      console.error('Failed to delete staff:', err);
      setError('Failed to delete staff: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  }, [staff]);

  // Manual entry handler
  const handleManualEntry = useCallback(async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}/api/attendance/manual`, manualEntry, { headers: getAuthHeaders() });
      setManualEntry({
        employeeId: '',
        date: '',
        checkIn: '',
        checkOut: '',
        status: 'present',
        remarks: '',
      });
      setSuccess('Manual entry submitted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
      
      // Silent refresh
      fetchAttendanceSummary().catch(console.error);
    } catch (err) {
      console.error('Failed to submit manual entry:', err);
      setError('Failed to submit manual entry: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  }, [manualEntry, fetchAttendanceSummary]);

  // FIXED: Complete shift management handler - Modern flow only
  const handleAddShift = useCallback(async (e, shiftData) => {
    e.preventDefault();
    
    // Require shiftData parameter (no legacy flow)
    if (!shiftData) {
      console.error('❌ handleAddShift called without shiftData parameter');
      setError('Invalid shift data. Please refresh the page and try again.');
      return;
    }
    
    try {
      setActionLoading(true);
      
      // Post shift with ALL fields (including isHalfDay and isFullDayReference)
      await axios.post(`${API_BASE_URL}/api/attendance/shifts`, shiftData, { 
        headers: getAuthHeaders() 
      });

      // Refresh shifts list
      await fetchShifts();
      
      setSuccess('Shift created successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
      
    } catch (err) {
      console.error('Failed to create shift:', err);
      setError('Failed to create shift: ' + (err.response?.data?.message || err.message));
      throw err; // Re-throw so ShiftManagement can handle it
    } finally {
      setActionLoading(false);
    }
  }, [fetchShifts]);
 

  const handleDeleteShift = useCallback(async (shiftId) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) {
      return;
    }

    try {
      setActionLoading(true);
      await axios.delete(`${API_BASE_URL}/api/attendance/shifts/${shiftId}`, { headers: getAuthHeaders() });
      
      setSuccess('Shift deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
      
      // Refresh shifts data
      await fetchShifts();
      
    } catch (err) {
      console.error('Failed to delete shift:', err);
      setError('Failed to delete shift: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  }, [fetchShifts]);

  // FIXED: Complete leave management handlers
  const handleAddLeave = useCallback(async (e) => {
    e.preventDefault();
    if (!newLeave.employeeId || !newLeave.startDate || !newLeave.endDate) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setActionLoading(true);
      
      const response = await axios.post(`${API_BASE_URL}/api/attendance/leaves`, {
        employeeId: newLeave.employeeId,
        startDate: newLeave.startDate,
        endDate: newLeave.endDate,
        reason: newLeave.reason,
        leaveType: 'Annual'
      }, { headers: getAuthHeaders() });
      
      // Add to local state
      setLeaveRequests(prev => [response.data, ...prev]);
      
      // Clear form
      setNewLeave({ employeeId: '', startDate: '', endDate: '', reason: '' });
      
      setSuccess('Leave request added successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
      
    } catch (err) {
      console.error('Failed to add leave request:', err);
      setError('Failed to add leave request: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  }, [newLeave]);

  const handleEditLeave = useCallback(async (e) => {
    e.preventDefault();
    if (!editingLeave) return;

    try {
      setActionLoading(true);
      
      const response = await axios.put(`${API_BASE_URL}/api/attendance/leaves/${editingLeave._id}`, {
        employeeId: editingLeave.employeeId,
        startDate: editingLeave.startDate,
        endDate: editingLeave.endDate,
        reason: editingLeave.reason,
        status: editingLeave.status || 'Pending'
      }, { headers: getAuthHeaders() });
      
      // Update local state
      setLeaveRequests(prev => prev.map(leave => 
        leave._id === editingLeave._id ? response.data : leave
      ));
      
      setEditingLeave(null);
      setNewLeave({ employeeId: '', startDate: '', endDate: '', reason: '' });
      
      setSuccess('Leave request updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
      
    } catch (err) {
      console.error('Failed to update leave request:', err);
      setError('Failed to update leave request: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  }, [editingLeave]);

  const handleDeleteLeave = useCallback(async (leaveId) => {
    if (!window.confirm('Are you sure you want to delete this leave request?')) {
      return;
    }

    try {
      setActionLoading(true);
      await axios.delete(`${API_BASE_URL}/api/attendance/leaves/${leaveId}`, { headers: getAuthHeaders() });
      
      // Remove from local state
      setLeaveRequests(prev => prev.filter(leave => leave._id !== leaveId));
      
      setSuccess('Leave request deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
      
    } catch (err) {
      console.error('Failed to delete leave request:', err);
      setError('Failed to delete leave request: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  }, []);

  // FIXED: Complete holiday management handlers
  const handleAddHoliday = useCallback(async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!newHoliday.name || !newHoliday.date) {
      setError('Holiday name and date are required');
      return;
    }
    
    try {
      setActionLoading(true);
      
      // Send request to backend
      const response = await axios.post(
        `${API_BASE_URL}/api/attendance/holidays`, 
        {
          name: newHoliday.name.trim(),
          date: newHoliday.date
        }, 
        { headers: getAuthHeaders() }
      );
      
      // Update local state with the new holiday
      setHolidays(prevHolidays => [...prevHolidays, response.data]);
      
      // Clear form
      setNewHoliday({ name: '', date: '' });
      
      // Show success message
      setSuccess('Holiday added successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
      
      console.log('Holiday added:', response.data);
      
    } catch (err) {
      console.error('Failed to add holiday:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to add holiday';
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [newHoliday]);

  const handleDeleteHoliday = useCallback(async (holidayId) => {
    if (!holidayId) {
      setError('Invalid holiday ID');
      return;
    }
    
    // Optional: Add confirmation dialog
    if (!window.confirm('Are you sure you want to delete this holiday?')) {
      return;
    }
    
    try {
      setActionLoading(true);
      
      // Send delete request to backend
      await axios.delete(
        `${API_BASE_URL}/api/attendance/holidays/${holidayId}`, 
        { headers: getAuthHeaders() }
      );
      
      // Remove from local state
      setHolidays(prevHolidays => 
        prevHolidays.filter(holiday => holiday._id !== holidayId)
      );
      
      // Show success message
      setSuccess('Holiday deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setError('');
      
      console.log('Holiday deleted:', holidayId);
      
    } catch (err) {
      console.error('Failed to delete holiday:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete holiday';
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Force device sync with silent operation
  const forceDeviceSync = useCallback(async () => {
    try {
      setActionLoading(true);
      await axios.post(`${API_BASE_URL}/api/adms/force-sync`, {}, { headers: getAuthHeaders() });
      setSuccess('Device sync initiated. Data will refresh automatically.');
      setTimeout(() => {
        setSuccess('');
        // Silent refresh after delay
        fetchAttendanceSummary().catch(console.error);
      }, 3000);
    } catch (error) {
      console.error('Error forcing device sync:', error);
      setError('Failed to initiate device sync');
    } finally {
      setActionLoading(false);
    }
  }, [fetchAttendanceSummary]);

  // PDF generation
  const generateReport = useCallback(() => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Attendance Report', 14, 22);

    const tableData = attendanceRecords.map(record => [
      getStaffName(record),
      getStaffPosition(record),
      formatUTCDate(record.date),
      record.checkIn ? formatUTCTime(record.checkIn) : '-',
      record.checkOut ? formatUTCTime(record.checkOut) : '-',
      record.lateMinutes > 0 ? `${record.lateMinutes} min` : '-',
      record.earlyLeaveMinutes > 0 ? `${record.earlyLeaveMinutes} min` : '-',
      record.absent ? 'Yes' : 'No',
    ]);

    doc.autoTable({
      head: [['Staff Name', 'Position', 'Date', 'Check-In', 'Check-Out', 'Late', 'Early Leave', 'Absent']],
      body: tableData,
      startY: 30,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 10 },
    });

    doc.save(`attendance-report-${reportMode === 'day' ? filterDate : reportMode}.pdf`);
  }, [attendanceRecords, getStaffName, getStaffPosition, reportMode, filterDate]);

  // Show initial loading only briefly for authentication check
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg flex items-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="text-gray-700">Loading...</span>
        </div>
      </div>
    );
  }

  // Main render - page loads immediately with default data
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Attendance Management</h1>
              <p className="text-gray-600">Ella's Place Salon, Spa & Ladies Gym - Staff & Attendance Management</p>
            </div>
            <div className="flex items-center gap-3">
              {dataLoading && (
                <div className="flex items-center gap-2 text-blue-600 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm font-medium">Syncing data...</span>
                </div>
              )}
              <div className="text-xs text-gray-500 bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm">
                {staff.length > 0 ? `${staff.length} staff loaded` : 'Loading staff...'}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            {success}
          </div>
        )}

        {/* Action Loading Overlay - Only shows for user actions */}
        {actionLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              Processing...
            </div>
          </div>
        )}

        <div className="mt-4 sticky top-0 z-10 bg-white shadow-lg mb-8">
          <div className="flex items-center justify-between h-16">
            <div className="hidden sm:flex space-x-4">
              {sidebarItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeSection === item.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className="text-lg" />
                  {item.label}
                </button>
              ))}
            </div>
            <div className="sm:hidden">
              <button onClick={() => setIsNavOpen(!isNavOpen)} className="p-2 text-gray-600 hover:text-gray-900">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isNavOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16m-7 6h7'} />
                </svg>
              </button>
            </div>
          </div>
          {isNavOpen && (
            <div className="sm:hidden bg-white border-t border-gray-200">
              {sidebarItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id);
                    setIsNavOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-100 ${
                    activeSection === item.id ? 'bg-blue-50 text-blue-600' : ''
                  }`}
                >
                  <item.icon className="text-lg" />
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* DASHBOARD SECTION */}
        {activeSection === 'dashboard' && (
          <Dashboard
            dashboardStats={dashboardStats}
            chartData={chartData}
            deviceStatus={deviceStatus}
            staff={staff}
            attendanceRecords={attendanceRecords}
            liveLogs={liveLogs}
            getStaffName={getStaffName}
            getStaffPosition={getStaffPosition}
            generateReport={generateReport}
            forceDeviceSync={forceDeviceSync}
            loading={dataLoading}
            reportMode={reportMode}
            setReportMode={setReportMode}
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            customStartDate={customStartDate}
            setCustomStartDate={setCustomStartDate}
            customEndDate={customEndDate}
            setCustomEndDate={setCustomEndDate}
            reportPage={reportPage}
            setReportPage={setReportPage}
            reportTotalPages={reportTotalPages}
            fetchAttendanceSummary={fetchAttendanceSummary}
            sevenDayData={sevenDayData}
            loadingSevenDay={loadingSevenDay}
          />
        )}

        {/* STAFF MANAGEMENT SECTION */}
        {activeSection === 'staff' && (
          <StaffManagement
            staff={staff}
            newStaff={newStaff}
            setNewStaff={setNewStaff}
            editingStaff={editingStaff}
            setEditingStaff={setEditingStaff}
            departments={departments}
            positions={positions}
            handleAddStaff={handleAddStaff}
            handleEditStaff={handleEditStaff}
            handleDeleteStaff={handleDeleteStaff}
            generateEmployeeId={generateEmployeeId}
            loading={actionLoading}
          />
        )}

        {/* DEVICE MANAGEMENT SECTION */}
        {activeSection === 'devices' && (
          <DeviceManagement staff={staff} />
        )}

        {/* SHIFT MANAGEMENT SECTION - Now includes Manual Adjustment */}
        {activeSection === 'shifts' && (
          <ShiftManagement
            shifts={shifts}
            newShift={newShift}
            setNewShift={setNewShift}
            editingShift={editingShift}
            setEditingShift={setEditingShift}
            staff={staff}
            shiftPage={shiftPage}
            setShiftPage={setShiftPage}
            shiftTotalPages={shiftTotalPages}
            shiftStaffFilter={shiftStaffFilter}
            setShiftStaffFilter={setShiftStaffFilter}
            selectedStaff={selectedStaff}
            setSelectedStaff={setSelectedStaff}
            handleAddShift={handleAddShift}
            handleDeleteShift={handleDeleteShift}
            fetchShifts={fetchShifts}
            manualEntry={manualEntry}
            setManualEntry={setManualEntry}
            handleManualEntry={handleManualEntry}
            loading={actionLoading}
          />
        )}

        {/* HOLIDAYS & LEAVE SECTION - Combined */}
        {activeSection === 'holidayleave' && (
          <HolidayLeave
            staff={staff}
            leaveRequests={leaveRequests}
            newLeave={newLeave}
            setNewLeave={setNewLeave}
            editingLeave={editingLeave}
            setEditingLeave={setEditingLeave}
            handleAddLeave={handleAddLeave}
            editLeave={handleEditLeave}
            handleUpdateLeave={handleEditLeave}
            deleteLeave={handleDeleteLeave}
            holidays={holidays}
            newHoliday={newHoliday}
            setNewHoliday={setNewHoliday}
            handleAddHoliday={handleAddHoliday}
            deleteHoliday={handleDeleteHoliday}
            getStaffName={getStaffName}
            loading={actionLoading}
          />
        )}

        {/* REPORTS SECTION */}
        {activeSection === 'reports' && (
          <Reports
            attendanceRecords={attendanceRecords}
            staff={staff}
            getStaffName={getStaffName}
            getStaffPosition={getStaffPosition}
            generateReport={generateReport}
            reportMode={reportMode}
            setReportMode={setReportMode}
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            customStartDate={customStartDate}
            setCustomStartDate={setCustomStartDate}
            customEndDate={customEndDate}
            setCustomEndDate={setCustomEndDate}
            reportPage={reportPage}
            setReportPage={setReportPage}
            reportTotalPages={reportTotalPages}
            fetchAttendanceSummary={fetchAttendanceSummary}
            loading={dataLoading}
          />
        )}
      </div>
    </div>
  );
}

export default Attendance;